import React, { useState, useEffect, useRef } from "react";
import { Form, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import FormSelect from "../Form/FormSelect";
import { useForm } from "react-hook-form";
import { useSetRecoilState } from "recoil";
import { alertState } from "src/recoil/atoms/global";
import TagsManageTable from "src/components/TestPackage/components/TagsManageTable";
import TooltipWrapper from "../TooltipWrapper";
import useScrollTo from "src/Hooks/useScrollTo";
import useGetTestSequenceTags from "src/components/TestPackage/useGetTestSequenceTags";
import useUpdateTestSequenceTags from "src/components/TestPackage/useUpdateTestSequenceTags";
import { getLastTagColor, getRandomColor } from "src/utils/common";
import useGetTestPackageList from "src/components/TestPackage/useGetTestPackageList";
import isEmpty from "lodash/isEmpty";

const FORM_NAME = {
  SELECTED_TEST_PACKAGE: "selected_test_package",
  TAGS: "tags",
};

const ModalManageTestSequenceTag = ({ testPackageData, isOpen, toggle, onSaveSuccess }) => {
  const { t } = useTranslation("test-package", "common");
  const [isSubmit, setIsSubmit] = useState(false);
  const [isImportingTags, setIsImportingTags] = useState(false);
  const [isAddingNewRow, setIsAddingNewRow] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [showValidationError, setShowValidationError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const newRowRef = useRef(null);
  const tableContainerRef = useRef(null);
  const { scrollToElement } = useScrollTo();
  const [tags, setTags] = useState([]);
  const [newTagColor, setNewTagColor] = useState(getLastTagColor(tags));
  const showAlert = useSetRecoilState(alertState);
  const {
    control,
    setValue,
    handleSubmit,
    register,
    watch,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      [FORM_NAME.SELECTED_TEST_PACKAGE]: "",
      [FORM_NAME.TAGS]: tags,
    },
  });

  const watchTestPackageImportId = watch(FORM_NAME.SELECTED_TEST_PACKAGE);

  const { isLoading: isLoadingTags } = useGetTestSequenceTags(testPackageData?.id, {
    enabled: !!testPackageData?.id && isOpen,
    refetchOnWindowFocus: false,
    setTags,
    reset: (tags) =>
      reset({
        [FORM_NAME.TAGS]: tags,
        [FORM_NAME.SELECTED_TEST_PACKAGE]: watchTestPackageImportId || "",
      }),
    getRandomColor,
    showAlert,
    t,
  });

  const { refetch: refetchImportData } = useGetTestSequenceTags(watchTestPackageImportId, {
    enabled: false,
  });

  const { testPackageList: testPackageOptions, isFetchingTestPackageList } = useGetTestPackageList({
    key: "getTestPackageOptions",
    isSelectOptions: true,
    exceptionId: testPackageData?.id,
    hasTags: true,
    yamcs_package_id: testPackageData?.yamcs_package_id,
    refetchOnWindowFocus: false,
  });

  const saveTagsMutation = useUpdateTestSequenceTags({
    showAlert,
    t,
    toggle,
    onSettled: () => {
      setIsSubmit(false);
    },
    onSuccess: () => {
      onSaveSuccess();
    },
  });

  const getNewId = (tags, offset = 0) => {
    const maxId = tags.length > 0 ? Math.max(...tags.map((tag) => tag?.display_order)) : 0;
    return maxId + 1 + offset;
  };

  const handleSaveSequenceTag = () => {
    setIsSubmit(true);
    const tagsWithTestPackageId = tags.map((tag, index) => ({
      ...tag,
      display_order: index + 1,
      test_package_id: testPackageData?.id,
    }));
    saveTagsMutation.mutate({ testPackageId: testPackageData?.id, tags: tagsWithTestPackageId });
  };

  const handleCancel = () => {
    toggle();
    document.body.click();
  };

  const isExistingTag = (tagList, selectedTag) => {
    return tagList.find((tag) => tag?.name?.toLowerCase() === selectedTag?.trim()?.toLowerCase());
  };

  const handleDeleteTag = (tagDisplayOrder) => {
    setTags((prevTags) => {
      const newTags = prevTags.filter((tag) => tag?.display_order !== tagDisplayOrder);
      return newTags;
    });
  };

  const validateImportSelection = () => {
    if (!watchTestPackageImportId) {
      showAlert({
        type: "error",
        message: t("list.tag_modal.please_select_test_package"),
      });
      return false;
    }
    return true;
  };

  const fetchImportData = async () => {
    const response = await refetchImportData();
    return response?.data;
  };

  const transformImportedTags = (items) => {
    return items.map((tag, index) => ({
      display_order: getNewId(tags, index),
      color: tag.color || getRandomColor(),
      name: tag.name,
    }));
  };

  const addImportedTagsToList = (importedTags) => {
    setTags((prevTags) => [...prevTags, ...importedTags]);
    setValue(FORM_NAME.SELECTED_TEST_PACKAGE, "");
  };

  const showNoTagsAlert = () => {
    showAlert({
      status: true,
      type: "danger",
      message: t("list.tag_modal.no_tags_to_import"),
    });
  };

  const processImportedData = (importData) => {
    if (!isEmpty(importData?.data?.items)) {
      const importedTags = transformImportedTags(importData.data.items);
      addImportedTagsToList(importedTags);
    } else {
      showNoTagsAlert();
    }
  };

  const handleImportTags = async () => {
    if (!validateImportSelection()) {
      return;
    }

    setIsImportingTags(true);

    const importData = await fetchImportData();
    processImportedData(importData);

    setIsImportingTags(false);
  };

  const handleMoveTag = (dragIndex, hoverIndex) => {
    const dragTag = tags[dragIndex];
    const newTags = [...tags];
    newTags.splice(dragIndex, 1);
    newTags.splice(hoverIndex, 0, dragTag);

    const reorderedTags = newTags.map((tag, index) => ({
      ...tag,
      display_order: index + 1,
    }));

    setTags(reorderedTags);
  };

  const handleAddNewRow = () => {
    setIsAddingNewRow(true);
    setNewTagName("");
    setNewTagColor(getColorOfLastRecord(tags));
    setShowValidationError(false);
    setErrorMessage("");
  };

  const handleCancelNewRow = () => {
    setIsAddingNewRow(false);
    setNewTagName("");
    setNewTagColor("");
    setShowValidationError(false);
    setErrorMessage("");
  };

  const getColorOfLastRecord = (tags) => {
    return tags.length > 0 ? tags[tags.length - 1].color : "#00DDFF";
  };

  const handleSaveNewRow = () => {
    if (!newTagName.trim()) {
      setShowValidationError(true);
      setErrorMessage(t("list.tag_modal.tag_name_required"));
      scrollToElement(newRowRef, tableContainerRef);
      return;
    }

    if (newTagName.trim().length > 255) {
      setShowValidationError(true);
      setErrorMessage(t("maxlength", { max_length: 255 }));
      scrollToElement(newRowRef, tableContainerRef);
      return;
    }

    if (isExistingTag(tags, newTagName)) {
      setShowValidationError(true);
      setErrorMessage(t("list.tag_modal.tag_already_exists"));
      scrollToElement(newRowRef, tableContainerRef);
      return;
    }

    const newTag = {
      display_order: getNewId(tags),
      color: newTagColor || getColorOfLastRecord(tags),
      name: newTagName.trim(),
    };

    const newTags = [...tags, newTag];
    setTags(newTags);

    setIsAddingNewRow(false);
    setNewTagName("");
    setNewTagColor("");
    setShowValidationError(false);
    setErrorMessage("");
  };

  const handleNewTagNameChange = (value) => {
    setNewTagName(value);
    if (showValidationError && value.trim()) {
      setShowValidationError(false);
      setErrorMessage("");
    }
  };

  const handleNewTagColorChange = (color) => {
    setNewTagColor(color);
  };

  useEffect(() => {
    setValue(FORM_NAME.TAGS, tags, { shouldDirty: true });
  }, [tags, setValue]);

  return (
    <Modal centered isOpen={isOpen} toggle={toggle} size="lg" backdrop="static" className="tag-manage-modal">
      <ModalHeader>
        <div className="d-flex align-items-center">
          <span className="m-r-6 text-nowrap">{t("list.tag_modal.test_sequence_title")}</span>
          <TooltipWrapper
            id={`tooltip-tag-modal-title-${testPackageData?.id}`}
            tooltipText={testPackageData?.user_input_id || ""}
            checkOverflow={true}
          >
            <div className="overflow-hidden text-truncate important-max-width-by-px-200">{testPackageData?.user_input_id}</div>
          </TooltipWrapper>
          <span className="m-l-6 text-nowrap">{t("list.tag_modal.tags_manage_title")}</span>
        </div>
      </ModalHeader>
      <ModalBody className="overflow-auto p-b-0">
        {isLoadingTags ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
            <Spinner size="sm" />
            <span className="ms-2">{t("list.tag_modal.loading_tags")}</span>
          </div>
        ) : (
          <>
            <div className="p-x-16 mb-3">
              <div className="row mb-3">
                <div className="col">
                  <div className="d-flex justify-content-end align-items-center">
                    <FormSelect
                      name={FORM_NAME.SELECTED_TEST_PACKAGE}
                      control={control}
                      placeholder={`${t("list.tag_modal.import_tags_placeholder")}`}
                      options={testPackageOptions}
                      classGroup="mb-0 width-by-px-360"
                      disabled={isSubmit}
                      isLoading={isFetchingTestPackageList}
                      isSearchable
                      setValue={setValue}
                      shouldDirty={false}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-primary m-x-10 width-by-px-120"
                      onClick={handleImportTags}
                      disabled={isSubmit || isImportingTags || !watchTestPackageImportId}
                    >
                      {isImportingTags ? <Spinner size="sm" /> : t("list.tag_modal.import_tags")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <Form className="tag-manage-form p-x-16">
              <input type="hidden" {...register(FORM_NAME.TAGS)} />
              <div className="row">
                <div className="col-md-12">
                  <TagsManageTable
                    tags={tags}
                    onMoveTag={handleMoveTag}
                    onDeleteTag={handleDeleteTag}
                    isAddingNewRow={isAddingNewRow}
                    newTagName={newTagName}
                    onNewTagNameChange={handleNewTagNameChange}
                    onSaveNewRow={handleSaveNewRow}
                    onCancelNewRow={handleCancelNewRow}
                    showValidationError={showValidationError}
                    errorMessage={errorMessage}
                    newRowRef={newRowRef}
                    tableContainerRef={tableContainerRef}
                    newTagColor={newTagColor}
                    onNewTagColorChange={handleNewTagColorChange}
                  />
                  {!isAddingNewRow && (
                    <div className="d-flex justify-content-start mt-2">
                      <TooltipWrapper id="add-new-record" tooltipText={t("list.tag_modal.add_new_record")}>
                        <button
                          type="button"
                          className="btn p-1 shadow-sm border-2 border-secondary bg-white"
                          onClick={handleAddNewRow}
                          disabled={isSubmit}
                        >
                          <i className="bi bi-plus-lg f-s-24 text-dark"></i>
                        </button>
                      </TooltipWrapper>
                    </div>
                  )}
                </div>
              </div>
            </Form>
          </>
        )}
      </ModalBody>
      <ModalFooter className="justify-content-center p-a-16">
        <div className="d-flex justify-content-center p-a-16">
          <button type="button" className="btn btn-sm btn-secondary m-x-10 width-by-px-120" disabled={isSubmit} onClick={handleCancel}>
            {t("cancel")}
          </button>
          <button
            type="submit"
            className="btn btn-sm btn-primary m-x-10 width-by-px-120"
            disabled={isSubmit || !isDirty || isAddingNewRow}
            onClick={handleSubmit((data) => handleSaveSequenceTag(data))}
          >
            {isSubmit ? <Spinner size="sm" /> : t("save")}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ModalManageTestSequenceTag;
